<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="consignment-details-main" v-if="cnDetails">
    <div class="content-container" >
      <div class="header">
        <div class="title">
          Summary Details for Consignment {{cnDetails[0].ConsignmentNo}}/{{cnDetails[0].TotalCount}}
        </div>
      </div>
      <div class="body-container" :class="{ 'is-loading': isLoading }">
        <div class="loading-overlay" v-if="isLoading">
          <div class="loading-spinner"></div>
        </div>
        <div class="cn-details-card">
          <div class="header" :style="cnDetailsCardHeaderStyle">
            <div class="label">
              Consignment Details
            </div>
            <img class="expand-all" src="https://dsmlib.logistics.systems/Icons/Expand.png" alt="Expand All" title="Expand All" @click="expandAllBoxContainers">
            <img class="collapse-all" src="https://dsmlib.logistics.systems/Icons/Collapse.png" alt="Collapse All" title="Collapse All" @click="collapseAllBoxContainers">
          </div>
          <div class="details-container">
            <div class="details-table-container">
              <table class="details-table">
                <tr>
                  <td class="label">Updated By</td>
                  <td class="colon">:</td>
                  <td class="details"><b>{{cnDetails[0].LastUserID}}</b></td>

                  <td class="spacer"/>

                  <td class="label">Last Update</td>
                  <td class="colon">:</td>
                  <td class="details"><b>{{cnDetails[0].LastDateUpdated}}</b></td>
                </tr>
                <tr>
                  <td class="label">Container No</td>
                  <td class="colon">:</td>
                  <td class="details"><b>{{cnDetails[0].ContainerNo}}</b></td>

                  <td class="spacer"/>

                  <td class="label">Request No</td>
                  <td class="colon">:</td>
                  <td class="details"><b>{{cnDetails[0].RequestNo}}</b></td>
                </tr>
              </table>
            </div>
            <div class="cn-details">
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="cnDetailsBoxContainersCollapsed.containerInfo"
                title="Container Information"
                ref="containerInfo"
                @collapseArrowClicked="boxContainerCollapseArrowClicked">
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label" style="width:4%">Commodity</td>
                      <td class="colon" style="width:0%">:</td>
                      <td class="details" colspan="5"><b>{{cnDetails[0].Commodity}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Size/Type</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ContSizeType}}</b></td>

                      <td class="spacer"/>

                      <td class="label">{{cnDetails[0].DocTitle}}</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].DocNo}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Operator</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].Operator}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Status</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].CurrentStatus}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Handling Type</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].HandlingType}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Del Type</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].DeliveryType}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Seal No</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].SealNo}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Trip</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].Trip}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Fumigation</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].Fumigation}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Office Hours?</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].OfficeHrs}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Timbang</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].Timbang}}</b></td>
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="cnDetailsBoxContainersCollapsed.jobInfo"
                title="Job Information"
                ref="jobInfo"
                @collapseArrowClicked="boxContainerCollapseArrowClicked">
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label" style="width:4%">{{ cnDetails[0].JobTypeHeader === 'E' ? 'Shipper' : 'Consignee' }}</td>
                      <td class="colon" style="width:0%">:</td>
                      <td class="details" colspan="5"><b>{{cnDetails[0].ShprCsgnCode}} ({{cnDetails[0].ShprCsgnName}})</b></td>
                    </tr>
                    <tr>
                      <td class="label">Bill To</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].BillTo}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Job Type</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].JobType}}</b></td>

                    </tr>
                    <tr>
                      <td class="label">Pick Up</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].PickUp}} ({{cnDetails[0].PUArea}})</b></td>

                      <td class="spacer"/>

                      <td class="label">Drop Off</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].DropOff}} ({{cnDetails[0].DOArea}})</b></td>
                    </tr>
                    <tr>
                      <td class="label" style="width:7.5%">Cust Ref</td>
                      <td class="colon" style="width:0%">:</td>
                      <td class="details" colspan="5"><b>{{cnDetails[0].CustRef}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Request Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].RequestDate}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Plan Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].PlannedDate}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Plan PM</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].PlannedPM}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Plan Driver</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].PlannedDriver}}</b></td>
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="cnDetailsBoxContainersCollapsed.sendToInfo"
                title="Send To Information"
                ref="sendToInfo"
                @collapseArrowClicked="boxContainerCollapseArrowClicked">
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label">Send To</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].SendToCode}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Premises Is</td>
                      <td class="colon">:</td>
                      <td class="details" :style="{ color: premisesColor }">
                        <b>{{ premisesStatus }}</b>
                      </td>
                    </tr>
                    <tr>
                      <td class="details" colspan="7" v-html="SendToAdrress"/>
                    </tr>
                    <tr>
                      <td class="label">Contact Person</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ContactPerson != '-' ? cnDetails[0].ContactPerson : cnDetails[0].ContactEmail }}</b></td>

                      <td class="spacer"/>

                      <td class="label">Area</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].SendToArea}}</b></td>
                    </tr>
                    <tr v-if="cnDetails[0].Contact != '-' && cnDetails[0].ContactEmail != '-'">
                      <td class="label"></td>
                      <td class="colon"></td>
                      <td class="details"><b>{{cnDetails[0].ContactEmail}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Tel # (Send To)</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].Phone1}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Fax # (Send To)</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].Fax1}}</b></td>
                    </tr>
                    <tr v-if="cnDetails[0].Phone2 != '-' || cnDetails[0].Fax2 != '-'">
                      <td class="label"></td>
                      <td class="colon"></td>
                      <td class="details"><b>{{cnDetails[0].Phone2}}</b></td>

                      <td class="spacer"/>

                      <td class="label"></td>
                      <td class="colon"></td>
                      <td class="details"><b>{{cnDetails[0].Fax2}}</b></td>
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="cnDetailsBoxContainersCollapsed.voyageInfo"
                title="Voyage Information"
                ref="voyageInfo"
                @collapseArrowClicked="boxContainerCollapseArrowClicked">
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label">B/L #</td>
                      <td class="colon">:</td>
                      <td class="details" colspan="5"><b>{{cnDetails[0].BL_No}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Ship Line</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ShipLine}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Ship Call No</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].SCN}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Forwarding Agent</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ForwardingAgent}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Shipping Agent</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ShippingAgent}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Vessel Code</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].VesselNo}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Vessel Name</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].VesselName}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Voyage No</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].VoyNo}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Discharge Port</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].DischargePort}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Required Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].RequiredDate}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Closing Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].CloseDate}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Demurrage Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].DateDemurrage}}</b></td>

                      <td class="spacer"/>

                      <td class="label">Detention Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].DateDetention}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">ETA Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ETADate}}</b></td>

                      <td class="spacer"/>

                      <td class="label">ETD Date</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].ETDDate}}</b></td>
                    </tr>
                    <tr>
                      <td class="label">Booking Ref #</td>
                      <td class="colon">:</td>
                      <td class="details"><b>{{cnDetails[0].BookingRefNo}}</b></td>
                    </tr>
                  </table>
                </div>
              </BoxContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {
  },
  computed: {
    SendToAdrress: function() {
      if (!this.cnDetails || this.cnDetails.length === 0) {
        return "";
      }

      const detail = this.cnDetails[0];
      const address = [];
      
      // function to bold text
      const bold = (value) => `<b>${value}</b>`;
      
      if (detail.SendToCompany) address.push(bold(detail.SendToCompany));
      if (detail.SendToAddr1) address.push(bold(detail.SendToAddr1));
      if (detail.SendToAddr2) address.push(bold(detail.SendToAddr2));
      if (detail.SendToAddr3) address.push(bold(detail.SendToAddr3));
      
      const addr1 = [];
      if (detail.SendToPostCode) addr1.push(detail.SendToPostCode);
      if (detail.SendToCity) addr1.push(detail.SendToCity);
      
      if (addr1.length > 0) {
        address.push(bold(addr1.join(", "))); 
      }
      
      const addr2 = [];
      if (detail.SendToState) addr2.push(bold(detail.SendToState));
      if (detail.SendToCountry) addr2.push(bold(detail.SendToCountry));

      if (addr2.length > 0) {
        address.push(bold(addr2.join(", "))); 
      }
      
      return address.join("<br>");
    },
    cnDetailsCardHeaderStyle: function() {
      if (
        !this.cnDetailsCardHeaderBackgroundColor ||
        this.cnDetailsCardHeaderBackgroundColor === "white" || 
        !this.cnDetailsCardHeaderBackgroundColor.startsWith("#")
      ) {
        return {
          "background-color": "white",
          "color": "black",
        };
      }

      let backgroundColor = this.cnDetailsCardHeaderBackgroundColor.replace("#", "");
      if (backgroundColor.length == 3) { // if there are only 3 hex digits
        backgroundColor = backgroundColor[0] + backgroundColor[0] + backgroundColor[1] + backgroundColor[1] + backgroundColor[2] + backgroundColor[2];
      }

      const red = parseInt(backgroundColor.slice(0, 2), 16); // convert from hex to decimal
      const green = parseInt(backgroundColor.slice(2, 4), 16);
      const blue = parseInt(backgroundColor.slice(4, 6), 16);

      let textColor;
      if (((red + green + blue) / 3) > 100) {
        textColor = 'black';
      } else {
        textColor = 'white';
      }

      return {
        "background-color": this.cnDetailsCardHeaderBackgroundColor,
        "color": textColor,
      };
    },
    premisesStatus: function() {
      return this.cnDetails?.[0]?.PremisesIs || ''
    },
    premisesColor: function() {
      return this.premisesStatus.toUpperCase() === 'UNSECURED'
        ? 'red'
        : ''
    }
  },
  data: function() {
    return {
      cnDetailsCardHeaderBackgroundColor: "white",
      cnDetailsBoxContainersCollapsed: {
        containerInfo: true,
        jobInfo: true,
        sendToInfo: true,
        voyageInfo: true
      },
    };
  },
  props: {
    cnDetails:{ type: Array, default: function() { return null; } }, // consignment details passed 
    // expected format for cnDetails: 
    // obobtainable from API endpoint: Service6G.svc/GetCNDetails
    // [
    //   {
    //     PUArea: "WP",
    //     DOArea: "WP",
    //     PickUp: "KMT",
    //     DropOff: "GFORTUNE",
    //     ConsignmentNo: "LI0137797-001",
    //     RequestNo: "B25005882",
    //     ContainerNo: "EMCU1611193",
    //     ContSizeType: "40HC",
    //     HandlingType: "N",
    //     DeliveryType: "TR",
    //     TotalCount: 3,
    //     CurrentStatus: "Pending Delivery",
    //     Operator: "EMC",
    //     Commodity: "WHEEL DISC",
    //     Trip: "1         ",
    //     PremisesIs: "UNSECURED",
    //     SendToCode: "WZ12",
    //     SendToArea: "BTGKL",
    //     SendToCompany: "WAREHOUSE ZURAH",
    //     SendToAddr1: "LOT PT803, JALAN ZURAH 7",
    //     SendToAddr2: "KAWASAN PERINDUSTRIAN ZURAH",
    //     SendToAddr3: "",
    //     SendToPostCode: "44200",
    //     SendToCity: "BATANG KALI",
    //     SendToState: "",
    //     SendToCountry: "",
    //     ContactPerson: "NISA",
    //     ContactEmail: "-",
    //     Phone1: "0102361980",
    //     Phone2: "-",
    //     Fax1: "-",
    //     Fax2: "-",
    //     RequiredDate: "2025-05-31 10:00",
    //     RequestDate: "2025-05-30 15:09",
    //     CloseDate: "-",
    //     ETADate: "2025-05-28 08:00",
    //     ETDDate: "-",
    //     DateDemurrage: "-",
    //     DateDetention: "-",
    //     PlannedDate: "-",
    //     SCN: "254SOE",
    //     VesselNo: "FIT694199",
    //     VesselName: "ITAL BONUS",
    //     ShipLine: "BS0557",
    //     ForwardingAgent: "TLS01",
    //     ShippingAgent: "BS0218",
    //     DischargePort: "JAKARTA",
    //     DocNo: "-",
    //     DocTitle: "Gate Pass",
    //     SealNo: "EMCPKW9374",
    //     OfficeHrs: 1,
    //     LastUserID: "HAZRIE01            ",
    //     LastDateUpdated: "2025-05-30 15:09",
    //     JobType: "IMP",
    //     BillTo: "3000TL01",
    //     Fumigation: "N",
    //     CNLastUpStatus: "8001 ",
    //     JobRemarks: null,
    //     VoyNo: "0453-103N",
    //     BL_No: "",
    //     CustRef: "P2I02350525",
    //     ShprCsgnName: "PERODUA GLOBAL MANUFACTURING SDN BHD",
    //     ShprCsgnCode: "TL01PG01",
    //     BookingRefNo: "HASLS16250800605" 
    //   },
    // ];
    isLoading: { type: Boolean, default: false }, // loading state prop
  },
  methods: {
    expandAllBoxContainers: function() {
      if (this.isLoading) return;
      Object.keys(this.cnDetailsBoxContainersCollapsed).forEach(key => {
        this.cnDetailsBoxContainersCollapsed[key] = false;
      });
    },
    collapseAllBoxContainers: function() {
      if (this.isLoading) return;
      Object.keys(this.cnDetailsBoxContainersCollapsed).forEach(key => {
        this.cnDetailsBoxContainersCollapsed[key] = true;
      });
    },
    boxContainerCollapseArrowClicked: function(collapsed, refName) {
      if (this.isLoading) return;
      if (!collapsed) {
        this.$nextTick(() => {
          if (this.$refs[refName]) {
            this.$refs[refName].updateExpandedHeight();
          }
        });
      }
    },
    reupdateExpandedHeight: function() {
      Object.keys(this.$refs).forEach(ref => {
        if (this.$refs[ref] && this.$refs[ref].updateExpandedHeight) {
          this.$refs[ref].updateExpandedHeight();
        }
      });
    }
  },
  async mounted() {
    this.$bus.$on('backColorChanged', (backgroundColor) => { // getting backgroundColor from user options is asynch, therefore need to listen to an event
      this.cnDetailsCardHeaderBackgroundColor = backgroundColor; // and change the background color of the header once it has finished loading
    });
    this.cnDetailsCardHeaderBackgroundColor = this.$store.state["backgroundColor"]; // initialise background color to whatever is in vuex at the time of component mount
  },
  beforeDestroy() {
    this.$bus.$off('backColorChanged'); // stop listening to the event
  },
  watch: {
    cnDetails: {
      deep: true,
      handler: function() {
        this.$nextTick(() => {
          this.reupdateExpandedHeight();
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.consignment-details-main {
  > .content-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    > .header {
      margin-bottom: 5px;
      border-radius: 5px 5px 0 0;
      height: 30px;
      width: 100%;
      background-color: #535cda;
      padding-left: 5px;
      display: flex;
      align-items: center;

      > .title {
        color: white;
        font-weight: bold;
      }
    }

    > .body-container {
      position: relative;
      height: calc(100% - 30px);
      width: 100%;
      overflow: hidden;
      transition: opacity 0.3s ease;
  
      &.is-loading {
        opacity: 0.4;
        pointer-events: none;
      }

      .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        background-color: rgba(255, 255, 255, 0.1);
      
        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #535cda;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      }
    }
  }
}

.cn-details-card {
  margin-bottom: 10px;
  border: 1px solid black;
  border-radius: 5px;
  height: fit-content;
  width: 100%; 

  > .header {
    border-bottom: 1px solid black;
    border-radius: 5px 5px 0 0;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    > .label {
      font-weight: bold;
    }

    > .expand-all, > .collapse-all {
      position: absolute;
      height: 20px;
      width: 20px;
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        height: 40px;
        width: 40px;
      }
    }

    > .expand-all {
      right: 45px;

      &:hover {
        right: 35px;
      }
    }

    > .collapse-all {
      right: 15px;

      &:hover {
        right: 5px;
      }
    }
  }

  > .details-container {
    padding: 5px;
    display: flex;
    flex-direction: column;
  }

  > .cn-details{
    display: flex;
    flex-direction: column;
  }
}

.details-table-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .details-table {
    width: 100%;
  }
  .details-table .label {
    width: 22%;
  }
  .details-table .colon {
    width: 1%;
  }
  .details-table .details {
    width: 27%;
  }
  .details-table .spacer {
    width: .5%;
  }
}


</style>